import type { Reference } from '@medplum/fhirtypes'
import type { SeenMedplumClient } from '@seen/core/client'
import type {
  ExtendedResource,
  ExtendedResourceConstructor,
  ResourceOf,
} from '@seen/core/ExtendedResource'
import { invariant } from '@seen/core/invariant'
import { extractId } from '@seen/core/utils'
import {
  queryOptions,
  type UseSuspenseQueryOptions,
} from '@tanstack/react-query'
import { cacheKeys } from '#src/hooks/cacheKeys.ts'

export function readOptions<T extends ExtendedResource>(
  medplum: SeenMedplumClient,
  resourceClass: ExtendedResourceConstructor<T>,
  id: Reference<NoInfer<ResourceOf<T>>> | string | NoInfer<ResourceOf<T>>
): UseSuspenseQueryOptions<
  T,
  Error,
  T,
  ReturnType<typeof cacheKeys.readExtended>
> {
  const resolvedId = extractId(id)
  invariant(resolvedId, 'Invalid ID')
  return queryOptions({
    queryFn: async () => medplum.readResourceClass(resourceClass, resolvedId),
    // oxlint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: cacheKeys.readExtended(
      resourceClass.RESOURCE_TYPE,
      resolvedId,
      resourceClass.name
    ),
    meta: {
      queryParams: { _id: resolvedId },
      resourceClass,
    },
  })
}
